// McpIntegration.js
// Retell-style "MCP" management screen for Rexpt.
//
// What it does (mirrors Retell AI's MCP view):
//   • List every MCP server connected for this tenant (Monday, HubSpot-MCP, …).
//   • "+ Add MCP" → modal collects Label + MCP URL + (optional) bearer token,
//     then the backend adds the server AND auto-discovers its tools.
//   • Per server: expand to see the discovered tools and toggle each ON/OFF
//     FOR THE CURRENTLY SELECTED AGENT. Saving pushes those tools into the
//     agent's Retell general_tools (backend enableTools → mcpRetellSync).
//   • Re-discover / Edit / Delete a server.
//
// Backend routes (see integrationRoutes.js, all auth = app JWT):
//   POST   /integration/mcp/servers
//   GET    /integration/mcp/servers?agentId=
//   POST   /integration/mcp/servers/:id/discover
//   PATCH  /integration/mcp/servers/:id
//   DELETE /integration/mcp/servers/:id
//   POST   /integration/mcp/servers/:id/tools/enable   { agentId, toolNames }

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import PopUp from "../Popup/Popup";
import styles from "./McpIntegration.module.css";
import HeaderBar from "../HeaderBar/HeaderBar";
import NewModal from "../NewModal/NewModal";

// import LeadBoardImport from "./LeadBoardImport"; // board → leads flow abhi band

const API_BASE = process.env.REACT_APP_API_BASE_URL; // e.g. https://.../api

// Fresh token each call — the app clears/rotates localStorage on login, so a
// module-level captured token can go stale (same reasoning as the Shopify flow).
function authHeaders() {
  const token = localStorage.getItem("token") || "";
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

// One-CCCCclick presets for MCP-supporting platforms, so the user doesn't have to
// remember each MCP URL. Clicking one pre-fills the Add form (the user supplies
// the token / completes OAuth, then it auto-discovers tools).
//
// Every URL below was live-probed with an MCP `initialize` request and returned
// a real MCP response (200 = open, or 401 = live but needs a token). All use the
// Streamable-HTTP transport (the /mcp path) that this app's client speaks. The
// `open: true` ones need NO token — handy for a quick test. Vendors move URLs
// over time, so if one stops discovering, re-check their docs. For anything not
// listed, "+ Add MCP" with a manual URL connects + discovers the same way.
const PRESETS = [
  // ── Work management / CRM / productivity ──
  { platform: "monday", label: "Monday.com", mcpUrl: "https://mcp.monday.com/mcp" },
  // { platform: "hubspot", label: "HubSpot", mcpUrl: "https://mcp.hubspot.com/anthropic" },
  // { platform: "linear", label: "Linear", mcpUrl: "https://mcp.linear.app/mcp" },
  // { platform: "notion", label: "Notion", mcpUrl: "https://mcp.notion.com/mcp" },
  // { platform: "asana", label: "Asana", mcpUrl: "https://mcp.asana.com/mcp" },
  // { platform: "atlassian", label: "Jira / Confluence", mcpUrl: "https://mcp.atlassian.com/v1/mcp" },
  // { platform: "intercom", label: "Intercom", mcpUrl: "https://mcp.intercom.com/mcp" },
  // { platform: "canva", label: "Canva", mcpUrl: "https://mcp.canva.com/mcp" },
  // // ── Payments / commerce ──
  // { platform: "stripe", label: "Stripe", mcpUrl: "https://mcp.stripe.com" },
  // { platform: "paypal", label: "PayPal", mcpUrl: "https://mcp.paypal.com/mcp" },
  // { platform: "square", label: "Square", mcpUrl: "https://mcp.squareup.com/mcp" },
  // { platform: "wix", label: "Wix", mcpUrl: "https://mcp.wix.com/mcp" },
  // { platform: "webflow", label: "Webflow", mcpUrl: "https://mcp.webflow.com/mcp" },
  // // ── Dev / infra / data ──
  // { platform: "github", label: "GitHub", mcpUrl: "https://api.githubcopilot.com/mcp/" },
  // { platform: "sentry", label: "Sentry", mcpUrl: "https://mcp.sentry.dev/mcp" },
  // { platform: "vercel", label: "Vercel", mcpUrl: "https://mcp.vercel.com" },
  // { platform: "supabase", label: "Supabase", mcpUrl: "https://mcp.supabase.com/mcp" },
  // { platform: "neon", label: "Neon", mcpUrl: "https://mcp.neon.tech/mcp" },
  // { platform: "cloudflare", label: "Cloudflare", mcpUrl: "https://bindings.mcp.cloudflare.com/mcp" },
  // { platform: "posthog", label: "PostHog", mcpUrl: "https://mcp.posthog.com/mcp" },
  // { platform: "globalping", label: "Globalping", mcpUrl: "https://mcp.globalping.dev/mcp" },
  // // ── Automation / scraping ──
  // { platform: "zapier", label: "Zapier", mcpUrl: "https://mcp.zapier.com/api/mcp/mcp" },
  // { platform: "apify", label: "Apify", mcpUrl: "https://mcp.apify.com" },
  // // ── No token needed (great for a quick test) ──
  // { platform: "context7", label: "Context7", mcpUrl: "https://mcp.context7.com/mcp", open: true },
  // { platform: "deepwiki", label: "DeepWiki", mcpUrl: "https://mcp.deepwiki.com/mcp", open: true },
  // { platform: "exa", label: "Exa Search", mcpUrl: "https://mcp.exa.ai/mcp", open: true },
  // { platform: "huggingface", label: "Hugging Face", mcpUrl: "https://huggingface.co/mcp", open: true },
];

const initialsOf = (name = "?") =>
  name.trim().slice(0, 2).toUpperCase() || "MC";

// ── Inline line-icons (16px, stroke = currentColor) ──────────────────────────
// A small consistent icon set replaces emoji — the single biggest "professional"
// upgrade. Each is a thin stroked SVG that inherits color from its button.
const Ic = {
  plus: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
  ),
  refresh: (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6" /></svg>
  ),
  edit: (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
  ),
  trash: (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /></svg>
  ),
  search: (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
  ),
  link: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" /></svg>
  ),
  form: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 8h8M8 12h8M8 16h5" /></svg>
  ),
  warn: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" /></svg>
  ),
  chevron: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
  ),
  server: (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="7" rx="2" /><rect x="3" y="13" width="18" height="7" rx="2" /><path d="M7 7.5h.01M7 16.5h.01" /></svg>
  ),
};

const McpIntegration = () => {
  const location = useLocation();

  // This screen manages tools FOR one agent. Prefer nav-state, fall back to the
  // globally-selected agent the rest of the app stores in sessionStorage.
  const agentId =
    location.state?.agentId ||
    sessionStorage.getItem("SelectAgentId") ||
    "";

  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // serverId currently open

  // Custom Popup state
  const [popupInfo, setPopupInfo] = useState({ type: "", message: "", show: false });
  const showPopup = (type, message) => setPopupInfo({ type, message, show: true });

  // Add / Edit modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = add mode
  const [form, setForm] = useState({ label: "", mcpUrl: "", authToken: "", platform: "custom" });
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState("");

  // Per-server tool toggle drafts + busy flags
  const [draftEnabled, setDraftEnabled] = useState({}); // { serverId: Set(toolName) }
  const [rowBusy, setRowBusy] = useState({}); // { serverId: bool }

  // Boards for the "bind a board to a tool" picker (tools that need a boardId).
  const [boardsByServer, setBoardsByServer] = useState({}); // { serverId: [{id,name}] }
  const [boardsLoading, setBoardsLoading] = useState({}); // { serverId: bool }

  // "Details to collect" drafts for create-type tools. Key = `${serverId}::${toolName}`.
  const [fieldDrafts, setFieldDrafts] = useState({}); // { key: ["name","phone",...] }
  const [newFieldForm, setNewFieldForm] = useState({}); // { [key]: { ask: "", type: "text", sourceType: "ai_variable", sourceKey: "" } }
  const [agentVars, setAgentVars] = useState([]); // agent's LLM dynamic variables
  // Outbound Caller: qualifying questions ke variables [{ key, label }] —
  // mapping dropdown ka "Qualifying Answers" group.
  const [questionVars, setQuestionVars] = useState([]);

  // UI-only: which tool descriptions are expanded, and the per-server tool search.
  const [descOpen, setDescOpen] = useState({}); // { `${serverId}::${toolName}`: bool }
  const [toolFilter, setToolFilter] = useState({}); // { serverId: query }
  const toggleDesc = (key) => setDescOpen((d) => ({ ...d, [key]: !d[key] }));

  // Auto-save status for the "details to collect" fields (no manual Save button).
  const [fieldsStatus, setFieldsStatus] = useState({}); // { key: "saving" | "saved" }
  // Serialize auto-saves PER TOOL so two blur-triggered saves never run at once.
  // Concurrent saves each auto-create the same board column → duplicate columns.
  // savingRef = a save is in-flight; pendingRef = another change queued to re-run;
  // latestFieldsRef = the newest field set to persist when the queued run fires.
  const savingRef = useRef({});
  const pendingRef = useRef({});
  const latestFieldsRef = useRef({});

  // Same single-in-flight/latest-wins guard, but for the ENABLE toggles — so a
  // tool auto-saves the moment you flip it (no separate "Save" click), and rapid
  // toggles coalesce into one settled request per server.
  const toolsSavingRef = useRef({});
  const toolsPendingRef = useRef({});
  const latestToolsRef = useRef({});

  // ── data load ──────────────────────────────────────────────────────────────
  const loadServers = useCallback(async () => {
    try {
      const qs = agentId ? `?agentId=${encodeURIComponent(agentId)}` : "";
      const res = await fetch(`${API_BASE}/integration/mcp/servers${qs}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (data?.success) setServers(data.servers || []);
      else showPopup("failed", data?.message || "Failed to load MCP servers");
    } catch (e) {
      console.error("[mcp] load failed", e);
      showPopup("failed", "Could not reach the server");
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  const fetchAgentVariables = useCallback(async (aid) => {
    try {
      const res = await fetch(`${API_BASE}/category/agent/${aid}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      const llmId = data?.agent?.llmId;
      if (llmId) {
        const llmRes = await fetch(`${API_BASE}/getLlmDetails`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ llmId }),
        });
        const llmData = await llmRes.json();
        const varsObj = llmData?.data?.default_dynamic_variables || {};
        let keys = [];
        if (varsObj && typeof varsObj === "object") {
          if (Array.isArray(varsObj)) {
            keys = varsObj.map((v) => v && (v.name || v.key)).filter(Boolean);
          } else {
            keys = Object.keys(varsObj);
          }
        }
        if (keys.length) {
          setAgentVars(keys);
        }
      }
    } catch (e) {
      console.error("[mcp] failed to load agent variables", e);
    }
  }, []);

  useEffect(() => {
    loadServers();
    if (agentId) {
      fetchAgentVariables(agentId);
      fetch(`${API_BASE}/integration/mcp/agent-variables?agentId=${encodeURIComponent(agentId)}`, {
        headers: authHeaders(),
      })
        .then((r) => r.json())
        .then((d) => setQuestionVars(Array.isArray(d?.variables) ? d.variables : []))
        .catch(() => setQuestionVars([]));
    }
  }, [loadServers, agentId, fetchAgentVariables]);

  // ── add / edit ───────────────────────────────────────────────────────────—
  const openAdd = (preset) => {
    setEditingId(null);
    setFormErr("");
    setForm(
      preset
        ? { label: preset.label, mcpUrl: preset.mcpUrl, authToken: "", platform: preset.platform }
        : { label: "", mcpUrl: "", authToken: "", platform: "custom" }
    );
    setModalOpen(true);
  };

  const openEdit = (server) => {
    setEditingId(server.id);
    setFormErr("");
    setForm({
      label: server.label || "",
      mcpUrl: server.mcpUrl || "",
      authToken: "", // never prefill secret; blank = keep existing
      platform: server.platform || "custom",
    });
    setModalOpen(true);
  };

  const submitForm = async () => {
    if (saving) return;
    if (!form.mcpUrl.trim()) {
      setFormErr("MCP URL is required.");
      return;
    }
    // Token is REQUIRED when adding a new server (Monday & most MCPs need it).
    // On edit, blank is allowed — it keeps the existing token.
    if (!editingId && !form.authToken.trim()) {
      setFormErr("Bearer token is required to connect.");
      return;
    }
    setSaving(true);
    setFormErr("");
    try {
      if (editingId) {
        // Edit: PATCH (blank token keeps the old one)
        const body = { label: form.label, mcpUrl: form.mcpUrl };
        if (form.authToken.trim()) body.authToken = form.authToken.trim();
        const res = await fetch(`${API_BASE}/integration/mcp/servers/${editingId}`, {
          method: "PATCH",
          headers: authHeaders(),
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!data?.success) throw new Error(data?.message || "Update failed");
        showPopup("success", "Server updated");
      } else {
        // Add + auto-discover
        const res = await fetch(`${API_BASE}/integration/mcp/servers`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            platform: form.platform,
            label: form.label || form.platform,
            mcpUrl: form.mcpUrl.trim(),
            authToken: form.authToken.trim() || undefined,
            agentId: agentId || null,
          }),
        });
        const data = await res.json();
        if (!data?.success) {
          // 502 = added but discovery failed → still refresh so the card shows.
          throw new Error(data?.message || "Could not add server");
        }
        showPopup("success", `Connected — ${data.count || 0} tools found`);
      }
      setModalOpen(false);
      await loadServers();
    } catch (e) {
      setFormErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── re-discover / delete ─────────────────────────────────────────────────—
  const rediscover = async (server) => {
    setRowBusy((b) => ({ ...b, [server.id]: true }));
    try {
      const res = await fetch(
        `${API_BASE}/integration/mcp/servers/${server.id}/discover`,
        { method: "POST", headers: authHeaders() }
      );
      const data = await res.json();
      if (!data?.success) throw new Error(data?.message || "Discovery failed");
      showPopup("success", `${data.count || 0} tools refreshed`);
      await loadServers();
    } catch (e) {
      showPopup("failed", e.message);
    } finally {
      setRowBusy((b) => ({ ...b, [server.id]: false }));
    }
  };

  const removeServer = async (server) => {
    if (!window.confirm(`Remove "${server.label}"? This disconnects its tools from all agents.`))
      return;
    setRowBusy((b) => ({ ...b, [server.id]: true }));
    try {
      const res = await fetch(`${API_BASE}/integration/mcp/servers/${server.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!data?.success) throw new Error(data?.message || "Delete failed");
      showPopup("success", "Server removed");
      if (expanded === server.id) setExpanded(null);
      await loadServers();
    } catch (e) {
      showPopup("failed", e.message);
    } finally {
      setRowBusy((b) => ({ ...b, [server.id]: false }));
    }
  };

  // ── tool toggles ─────────────────────────────────────────────────────────—
  const toggleExpand = (server) => {
    if (expanded === server.id) {
      setExpanded(null);
      return;
    }
    // Seed the draft ON-set from the tools' `enabled` flag (per this agent).
    const on = new Set((server.tools || []).filter((t) => t.enabled).map((t) => t.name));
    setDraftEnabled((d) => ({ ...d, [server.id]: on }));
    setExpanded(server.id);

    // If any tool needs a boardId, load boards for the picker (once).
    if ((server.tools || []).some(toolNeedsBoard) && !boardsByServer[server.id]) {
      fetchBoards(server.id);
    }
  };

  const toggleTool = (serverId, toolName) => {
    setDraftEnabled((d) => {
      const set = new Set(d[serverId] || []);
      set.has(toolName) ? set.delete(toolName) : set.add(toolName);
      const arr = Array.from(set);
      // AUTO-SAVE: flipping the switch persists + syncs immediately — no second
      // "Save for this agent" click. Deferred out of the reducer via microtask.
      queueMicrotask(() => persistTools(serverId, arr));
      return { ...d, [serverId]: set };
    });
  };

  // Persist a server's enabled-tool set for this agent. Serialized per server
  // (single in-flight + latest-wins) so rapid toggles never race, and coalesced
  // so we send only the final set. Silent by default (auto-save); the manual
  // button passes { silent:false } to show a confirmation popup.
  const persistTools = async (serverId, toolNames, { silent = true } = {}) => {
    if (!agentId) {
      showPopup("failed", "No agent selected — open this from an agent to link tools.");
      return;
    }
    latestToolsRef.current[serverId] = toolNames;
    if (toolsSavingRef.current[serverId]) {
      toolsPendingRef.current[serverId] = true;
      return;
    }
    toolsSavingRef.current[serverId] = true;
    setRowBusy((b) => ({ ...b, [serverId]: true }));
    try {
      do {
        toolsPendingRef.current[serverId] = false;
        const names = latestToolsRef.current[serverId] || [];
        const res = await fetch(
          `${API_BASE}/integration/mcp/servers/${serverId}/tools/enable`,
          {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({ agentId, toolNames: names }),
          }
        );
        const data = await res.json();
        if (!data?.success) throw new Error(data?.message || "Save failed");
      } while (toolsPendingRef.current[serverId]);
      if (!silent) showPopup("success", "Tools synced to your agent");
      await loadServers();
    } catch (e) {
      showPopup("failed", e.message);
    } finally {
      toolsSavingRef.current[serverId] = false;
      setRowBusy((b) => ({ ...b, [serverId]: false }));
    }
  };

  // ── board binding (tools that need a boardId) ────────────────────────────────
  // A tool "needs a board" when its input schema declares a boardId property.
  const toolNeedsBoard = (t) =>
    !!(t?.inputSchema && t.inputSchema.properties && t.inputSchema.properties.boardId);

  // Lazy-load the server's boards for the picker.
  const fetchBoards = useCallback(async (serverId) => {
    setBoardsLoading((b) => ({ ...b, [serverId]: true }));
    try {
      const res = await fetch(
        `${API_BASE}/integration/mcp/servers/${serverId}/boards`,
        { headers: authHeaders() }
      );
      const data = await res.json();
      if (data?.success) {
        setBoardsByServer((m) => ({ ...m, [serverId]: data.boards || [] }));
      }
    } catch (_) {
      /* leave empty — the picker shows "no boards" */
    } finally {
      setBoardsLoading((b) => ({ ...b, [serverId]: false }));
    }
  }, []);

  // ── "details to collect" for create-type tools ───────────────────────────────
  // A tool "collects fields" when its schema has a columnValues param (Monday
  // create tools) — the agent will gather those details from the caller.
  const toolCollectsFields = (t) =>
    !!(t?.inputSchema?.properties && t.inputSchema.properties.columnValues);

  const fieldKey = (serverId, toolName) => `${serverId}::${toolName}`;

  // Friendly, proper-cased label for an AI-variable key shown in the dropdown.
  // The stored key (value) stays the raw variable; only the DISPLAY changes.
  const aiVarLabel = (key) => {
    const k = String(key || "").toLowerCase();
    const map = {
      phone: "Secondary Phone",
      phone_number: "Secondary Phone",
      name: "Name",
      email: "Email",
      reason: "Reason",
      address: "Address",
      appointment_booked: "Appointment Booked",
      appointment_datetime: "Appointment Date & Time",
      appointment_date: "Appointment Date",
      appointment_time: "Appointment Time",
      lead_type: "Lead Type",
      message_text: "Message",
      company: "Company",
      budget: "Budget",
    };
    return (
      map[k] ||
      String(key || "")
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase())
    );
  };

  const getFieldDraft = (serverId, t) => {
    const key = fieldKey(serverId, t.name);
    if (fieldDrafts[key]) return fieldDrafts[key];
    // seed from saved config, else sensible defaults
    const saved = t.fieldConfig?.fields || [];
    return saved.length ? saved : [
      { ask: "Name", column: "name", type: "name", param: "name", mapping: { sourceType: "ai_variable", sourceKey: "name" } },
      { ask: "Phone", type: "phone", param: "phone", mapping: { sourceType: "system", sourceKey: "caller_phone" } },
      { ask: "Email", type: "email", param: "email", mapping: { sourceType: "ai_variable", sourceKey: "email" } }
    ];
  };

  const setFieldDraft = (serverId, toolName, next) =>
    setFieldDrafts((d) => ({ ...d, [fieldKey(serverId, toolName)]: next }));

  // Auto-saves the fields (called on blur / delete — no manual button). Silent
  // on success (shows a subtle inline "Saved" state); only errors pop up.
  //
  // CRITICAL: saves for one tool are SERIALIZED. Blurring field-by-field would
  // otherwise fire overlapping requests that each auto-create the same board
  // column before the other finished — creating duplicate columns (e.g. two
  // "Email" columns). We keep a single in-flight save per tool; any change made
  // while it runs is coalesced into ONE follow-up run with the latest fields.
  const saveFields = async (serverId, toolName, fields) => {
    const key = fieldKey(serverId, toolName);
    latestFieldsRef.current[key] = fields; // always remember the newest intent

    if (savingRef.current[key]) {
      pendingRef.current[key] = true; // a save is running — queue a re-run
      return;
    }
    savingRef.current[key] = true;

    try {
      do {
        pendingRef.current[key] = false;
        const toSave = latestFieldsRef.current[key] || [];
        const clean = [];
        const seenAsks = new Set();
        for (const f of toSave) {
          if (!f || !f.ask || !String(f.ask).trim()) continue;
          const normAsk = String(f.ask).trim().toLowerCase();
          if (seenAsks.has(normAsk)) continue;
          seenAsks.add(normAsk);
          clean.push(f);
        }
        if (!clean.length) break; // nothing meaningful to save yet

        setFieldsStatus((m) => ({ ...m, [key]: "saving" }));
        const res = await fetch(
          `${API_BASE}/integration/mcp/servers/${serverId}/tools/fields`,
          {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({ toolName, fields: clean }),
          }
        );
        const data = await res.json();
        if (!data?.success) throw new Error(data?.message || "Could not save fields");
        setFieldsStatus((m) => ({ ...m, [key]: "saved" }));
        // NOTE: no loadServers() here — a full refetch on every blur reflows the
        // card and flashes a loader; the local draft is the source of truth.
      } while (pendingRef.current[key]); // re-run once if changes arrived mid-save
    } catch (e) {
      setFieldsStatus((m) => ({ ...m, [key]: undefined }));
      showPopup("failed", e.message);
    } finally {
      savingRef.current[key] = false;
    }
  };

  // Pin (or clear) the board for one tool. Empty boardId unbinds it.
  const saveBoardBinding = async (serverId, t, boardId) => {
    const toolName = t.name;
    try {
      const res = await fetch(
        `${API_BASE}/integration/mcp/servers/${serverId}/tools/defaults`,
        {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            toolName,
            argDefaults: boardId ? { boardId } : {},
          }),
        }
      );
      const data = await res.json();
      if (!data?.success) throw new Error(data?.message || "Could not link board");

      // The moment a board is linked, AUTO-FILL the collect-fields from the board's
      // OWN columns — so "select a board" surfaces every column the board already
      // has. This also persists a fieldConfig, mapping each board column to an
      // inferred call variable.
      if (boardId) {
        let fields = [
          { ask: "Name", column: "name", type: "name", param: "name", mapping: { sourceType: "ai_variable", sourceKey: "name" } },
          { ask: "Phone", type: "phone", param: "phone", mapping: { sourceType: "system", sourceKey: "caller_phone" } },
          { ask: "Email", type: "email", param: "email", mapping: { sourceType: "ai_variable", sourceKey: "email" } }
        ];
        try {
          const colRes = await fetch(
            `${API_BASE}/integration/mcp/servers/${serverId}/boards/${boardId}/columns`,
            { headers: authHeaders() }
          );
          const colData = await colRes.json();
          if (colData?.success && Array.isArray(colData.columns)) {
            const SKIP_TYPES = new Set([
              "auto_number", "creation_log", "last_updated", "formula", "mirror",
              "button", "subtasks", "doc", "progress", "time_tracking", "vote",
            ]);
            const SKIP_TITLES = new Set([
              "call id", "call status", "call type", "caller phone", "number dialed",
              "call date", "duration", "duration (sec)", "duration (min)",
              "recording url", "lead classification", "leads classification",
            ]);
            const fromBoard = colData.columns
              .filter(
                (c) =>
                  !SKIP_TYPES.has(c.type) &&
                  !SKIP_TITLES.has(String(c.title || "").trim().toLowerCase())
              )
              .map((c) => {
                const title = String(c.title || "").trim();
                const titleLower = title.toLowerCase();
                let sourceType = "ai_variable";
                let sourceKey = titleLower.replace(/[^a-z0-9]+/g, "_");

                if (titleLower === "name" || titleLower === "client name" || titleLower === "customer name") {
                  sourceType = "ai_variable";
                  sourceKey = "name";
                } else if (titleLower.includes("phone") || titleLower.includes("mobile") || titleLower.includes("contact")) {
                  sourceType = "system";
                  sourceKey = "caller_phone";
                } else if (titleLower.includes("email") || titleLower.includes("mail")) {
                  sourceType = "ai_variable";
                  sourceKey = "email";
                } else if (titleLower.includes("date")) {
                  sourceType = "system";
                  sourceKey = "call_date";
                } else if (titleLower.includes("duration")) {
                  sourceType = "system";
                  sourceKey = "duration";
                } else if (titleLower.includes("summary") || titleLower.includes("notes") || titleLower.includes("description")) {
                  sourceType = "system";
                  sourceKey = "call_summary";
                }

                return {
                  ask: title,
                  column: c.id,
                  type: c.type || "text",
                  param: titleLower.replace(/[^a-z0-9]+/g, "_"),
                  mapping: { sourceType, sourceKey }
                };
              })
              .filter(Boolean);
            if (fromBoard.length) fields = fromBoard;
          }
        } catch (_) {
          /* fallback */
        }
        setFieldDraft(serverId, toolName, fields);
        await saveFields(serverId, toolName, fields);
      }

      showPopup("success", boardId ? "Board linked ✅ — fields filled from its columns" : "Board unlinked");
      await loadServers();
    } catch (e) {
      showPopup("failed", e.message);
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      {/* Sticky wrapper — scroll pe bhi back arrow hamesha dikhe */}
      <div className={styles.Forsticky}>
        <HeaderBar title="MCP Integrations" backgroundColor="#6524EB" color="#fff" />
      </div>

      <div className={styles.body}>
        {/* Show a gentle notice only when NO agent is selected (tools can't be
            enabled without one). The agent-id banner was removed per request. */}
        {!agentId && (
          <div className={`${styles.agentBanner} ${styles.agentWarn}`}>
            <span className={styles.bannerIcon}>{Ic.warn}</span>
            <span>
              No agent selected. You can still add servers, but open this screen
              from an agent to enable tools for it.
            </span>
          </div>
        )}

        <p className={styles.intro}>
          Connect any MCP-compatible server (Monday.com, HubSpot, Linear…). We
          auto-discover its tools; enable the ones your agent should be able to
          call on live calls.
        </p>

        <div className={styles.sectionRow}>
          <button className={styles.addBtn} onClick={() => openAdd(null)}>
            {Ic.plus} Add MCP
          </button>

        </div>

        {/* Quick presets */}
        {/* <div className={styles.presetRow}>
          {PRESETS.map((p) => (
            <button
              key={p.platform}
              className={styles.presetChip}
              onClick={() => openAdd(p)}
              title={p.open ? `${p.label} — no token needed` : `${p.label} — needs a token / OAuth`}
            >
              <span className={styles.presetPlus}>{Ic.plus}</span>
              {p.label}
              {p.open && <span className={styles.presetFree}>free</span>}
            </button>
          ))}
        </div> */}

        {loading ? (
          <div className={styles.centerLoad}>
            <span className={`${styles.spinner} ${styles.spinnerDark}`} />
          </div>
        ) : servers.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>{Ic.server}</div>
            <h4>No MCP servers yet</h4>
            <p>Add one above to give your agent new tools.</p>
          </div>
        ) : (
          servers.map((s) => {
            const isOpen = expanded === s.id;
            const busy = !!rowBusy[s.id];
            const draft = draftEnabled[s.id] || new Set();
            const enabledCount = (s.tools || []).filter((t) => t.enabled).length;
            const allTools = s.tools || [];
            // True while ANY field on this server is auto-saving — used to block
            // "Save for this agent" until the field save settles.
            const serverSaving = Object.keys(fieldsStatus).some(
              (k) => k.startsWith(`${s.id}::`) && fieldsStatus[k] === "saving"
            );
            const filterQ = (toolFilter[s.id] || "").trim().toLowerCase();
            const visibleTools = filterQ
              ? allTools.filter(
                (t) =>
                  t.name.toLowerCase().includes(filterQ) ||
                  (t.description || "").toLowerCase().includes(filterQ)
              )
              : allTools;
            return (
              <div key={s.id} className={styles.card}>
                <div className={styles.cardHead}>
                  <div className={styles.serverIcon}>{initialsOf(s.label)}</div>
                  <div className={styles.serverMeta}>
                    <p className={styles.serverName}>{s.label}</p>
                    <p className={styles.serverUrl}>{s.mcpUrl}</p>
                    <div className={styles.badges}>
                      <span
                        className={`${styles.badge} ${s.hasToken ? styles.badgeOk : styles.badgeWarn
                          }`}
                      >
                        <span
                          className={`${styles.statusDot} ${s.hasToken ? styles.dotOk : styles.dotWarn
                            }`}
                        />
                        {s.hasToken ? "Authenticated" : "No token"}
                      </span>
                      <span className={styles.badge}>{s.tools?.length || 0} tools</span>
                      {agentId && (
                        <span
                          className={`${styles.badge} ${enabledCount > 0 ? styles.badgeAccent : ""
                            }`}
                        >
                          {enabledCount} enabled
                        </span>
                      )}
                      <span className={styles.badgeMuted}>
                        {s.lastSyncedAt
                          ? `synced ${new Date(s.lastSyncedAt).toLocaleDateString()}`
                          : "never synced"}
                      </span>
                    </div>
                  </div>
                  <div className={styles.cardActions}>
                    <button
                      className={styles.iconBtn}
                      title="Re-discover tools"
                      disabled={busy}
                      onClick={() => rediscover(s)}
                    >
                      {busy ? <span className={`${styles.spinner} ${styles.spinnerDark}`} /> : Ic.refresh}
                    </button>
                    <button
                      className={styles.iconBtn}
                      title="Edit"
                      onClick={() => openEdit(s)}
                    >
                      {Ic.edit}
                    </button>
                    <button
                      className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                      title="Delete"
                      disabled={busy}
                      onClick={() => removeServer(s)}
                    >
                      {Ic.trash}
                    </button>
                  </div>
                </div>

                {/* Outbound Caller agent: is server ke board se leads uthao. */}
                {/* Monday board → leads wala flow abhi band hai (wapas chalu: ye comment hatao — app.js aur pounceRoutes.js me bhi).
                {s.agentType === "outbound" && <LeadBoardImport serverId={s.id} />}
                */}

                <button className={styles.toolsToggle} onClick={() => toggleExpand(s)}>
                  <span className={`${styles.toggleChevron} ${isOpen ? styles.toggleChevronUp : ""}`}>
                    {Ic.chevron}
                  </span>
                  {isOpen ? "Hide tools" : `Manage tools (${s.tools?.length || 0})`}
                </button>

                {isOpen && (
                  <>
                    {allTools.length > 0 && (
                      <div className={styles.toolsHeader}>
                        <div className={styles.searchWrap}>
                          <span className={styles.searchIcon}>{Ic.search}</span>
                          <input
                            className={styles.searchInput}
                            value={toolFilter[s.id] || ""}
                            placeholder={`Search ${allTools.length} tools…`}
                            onChange={(e) =>
                              setToolFilter((f) => ({ ...f, [s.id]: e.target.value }))
                            }
                          />
                          {filterQ && (
                            <button
                              type="button"
                              className={styles.searchClear}
                              title="Clear"
                              onClick={() =>
                                setToolFilter((f) => ({ ...f, [s.id]: "" }))
                              }
                            >
                              ✕
                            </button>
                          )}
                        </div>
                        <span className={styles.toolsCount}>
                          {draft.size} of {allTools.length} enabled
                        </span>
                      </div>
                    )}
                    <div className={styles.toolsBox}>
                      {allTools.length === 0 ? (
                        <p className={styles.emptyTools}>
                          No tools discovered. Try the refresh button — the token or URL
                          may be wrong.
                        </p>
                      ) : visibleTools.length === 0 ? (
                        <p className={styles.emptyTools}>
                          No tools match “{toolFilter[s.id]}”.
                        </p>
                      ) : (
                        visibleTools.map((t) => {
                          const dKey = `${s.id}::${t.name}`;
                          const on = draft.has(t.name);
                          const longDesc = (t.description || "").length > 110;
                          const descIsOpen = !!descOpen[dKey];
                          const fSaving = fieldsStatus[dKey] === "saving";
                          return (
                            <div
                              key={t.name}
                              className={`${styles.toolRow} ${on ? styles.toolRowOn : ""}`}
                            >
                              <div className={styles.toolInfo}>
                                <span className={styles.toolName}>{t.name}</span>
                                {t.description && (
                                  <p
                                    className={`${styles.toolDesc} ${!descIsOpen ? styles.toolDescClamp : ""
                                      }`}
                                  >
                                    {t.description}
                                  </p>
                                )}
                                {longDesc && (
                                  <button
                                    type="button"
                                    className={styles.toolMoreBtn}
                                    onClick={() => toggleDesc(dKey)}
                                  >
                                    {descIsOpen ? "Show less" : "Show more"}
                                  </button>
                                )}
                                {toolNeedsBoard(t) && draft.has(t.name) && (
                                  <div className={styles.boardBind}>
                                    <span className={styles.boardBindLabel}>
                                      {Ic.link} Board for this tool
                                    </span>
                                    <select
                                      className={styles.boardSelect}
                                      value={t.argDefaults?.boardId || ""}
                                      onChange={(e) =>
                                        saveBoardBinding(s.id, t, e.target.value)
                                      }
                                    >
                                      <option value="">
                                        {boardsLoading[s.id]
                                          ? "Loading boards…"
                                          : "— select a board —"}
                                      </option>
                                      {(boardsByServer[s.id] || []).map((b) => (
                                        <option key={b.id} value={b.id}>
                                          {b.name} ({b.id})
                                        </option>
                                      ))}
                                    </select>
                                    {!t.argDefaults?.boardId && (
                                      <span className={styles.boardWarn}>
                                        {Ic.warn} Pick a board so the agent uses the right id
                                      </span>
                                    )}
                                  </div>
                                )}

                                {toolCollectsFields(t) && draft.has(t.name) && (
                                  <div className={styles.fieldsBox}>
                                    <span className={styles.fieldsLabel}>
                                      {Ic.form} Details the agent should collect
                                      {fieldsStatus[fieldKey(s.id, t.name)] === "saving" && (
                                        <span className={styles.fieldsSaving}>Saving…</span>
                                      )}
                                      {fieldsStatus[fieldKey(s.id, t.name)] === "saved" && (
                                        <span className={styles.fieldsSaved}>✓ Saved</span>
                                      )}
                                    </span>

                                    <div className={styles.mappingExplainer}>
                                      <div className={styles.explainerCol}>
                                        <strong>Monday.com Board Column</strong>
                                        <span>(Column in your Monday.com board)</span>
                                      </div>
                                      <span className={styles.mappingArrow} style={{ alignSelf: "center", fontSize: "14px", fontWeight: "bold" }}>➔</span>
                                      <div className={styles.explainerCol}>
                                        <strong>Call Variable Source</strong>
                                        <span>(Select variable from Call/AI to map with)</span>
                                      </div>
                                    </div>

                                    {getFieldDraft(s.id, t).map((fieldObj, i) => {
                                      const toolKey = fieldKey(s.id, t.name);
                                      const mapping = fieldObj.mapping || { sourceType: "ai_variable", sourceKey: fieldObj.param || "" };
                                      const selectValue =
                                        !mapping.sourceType || mapping.sourceType === "skip"
                                          ? `ai_variable::${fieldObj.param || ""}`
                                          : `${mapping.sourceType}::${mapping.sourceKey}`;

                                      return (
                                        <div key={i} className={styles.mappingFieldRow}>
                                          <div className={styles.colInfoBlock}>
                                            <span className={styles.colTitleName}>{fieldObj.ask}</span>
                                            <span className={styles.colTypeBadge}>{fieldObj.type || "text"}</span>
                                          </div>

                                          <div className={styles.mappingSelectBlock}>
                                            <span className={styles.mappingArrow}>➔</span>
                                            <select
                                              className={styles.mappingSourceSelect}
                                              value={selectValue}
                                              onChange={async (e) => {
                                                const selected = e.target.value;
                                                const next = [...getFieldDraft(s.id, t)];
                                                const [sType, sKey] = selected.split("::");
                                                next[i] = {
                                                  ...fieldObj,
                                                  mapping: { sourceType: sType, sourceKey: sKey || "" }
                                                };
                                                setFieldDraft(s.id, t.name, next);
                                                setFieldsStatus((m) => ({ ...m, [toolKey]: undefined }));
                                                await saveFields(s.id, t.name, next);
                                              }}
                                            >
                                              <optgroup label="System Call Metadata">
                                                <option value="system::caller_phone">Caller Phone</option>
                                                <option value="system::number_dialed">Number Dialed</option>
                                                <option value="system::call_date">Call Date & Time</option>
                                                <option value="system::duration">Call Duration</option>
                                                <option value="system::recording_url">Recording URL</option>
                                                <option value="system::call_status">Call Status</option>
                                                <option value="system::call_summary">Call Summary (AI)</option>
                                                <option value="system::call_transcript">Call Transcript</option>
                                                <option value="system::lead_type">Lead Type / Classification</option>
                                                <option value="system::user_sentiment">User Sentiment</option>
                                                <option value="system::call_type">Call Type</option>
                                            {/* Booked / No answer / Wrong number / Not qualified / Follow-up needed / Other */}
                                            <option value="system::call_result">Call Result</option>
                                              </optgroup>
                                              {questionVars.length > 0 && (
                                                <optgroup label="Qualifying Answers">
                                                  {questionVars.map((v) => (
                                                    <option key={v.key} value={`ai_variable::${v.key}`} title={v.label}>
                                                      Answer: {v.key}
                                                    </option>
                                                  ))}
                                                </optgroup>
                                              )}
                                              <optgroup label="AI Variable (Agent Collects)">
                                                {agentVars.map((vname) => (
                                                  <option key={vname} value={`ai_variable::${vname}`}>AI Variable: {aiVarLabel(vname)}</option>
                                                ))}
                                                {!agentVars.includes("name") && <option value="ai_variable::name">AI Variable: Name</option>}
                                                {!agentVars.includes("phone") && <option value="ai_variable::phone">AI Variable: Secondary Phone</option>}
                                                {!agentVars.includes("email") && <option value="ai_variable::email">AI Variable: Email</option>}
                                                {!agentVars.includes("reason") && <option value="ai_variable::reason">AI Variable: Reason</option>}
                                                {!agentVars.includes("address") && <option value="ai_variable::address">AI Variable: Address</option>}
                                                {/* Appointment — call ke baad ke analysis se (book hui ho tabhi date/time bhare hote hain). */}
                                                {!agentVars.includes("appointment_booked") && <option value="ai_variable::appointment_booked">AI Variable: Appointment Booked</option>}
                                                {!agentVars.includes("appointment_datetime") && <option value="ai_variable::appointment_datetime">AI Variable: Appointment Date &amp; Time</option>}
                                                {!agentVars.includes("appointment_date") && <option value="ai_variable::appointment_date">AI Variable: Appointment Date</option>}
                                                {!agentVars.includes("appointment_time") && <option value="ai_variable::appointment_time">AI Variable: Appointment Time</option>}
                                                {mapping.sourceType === "ai_variable" &&
                                                  mapping.sourceKey &&
                                                  !agentVars.includes(mapping.sourceKey) &&
                                                  !["name", "phone", "email", "reason", "address"].includes(mapping.sourceKey) && (
                                                    <option value={`ai_variable::${mapping.sourceKey}`}>
                                                      AI Variable: {aiVarLabel(mapping.sourceKey)}
                                                    </option>
                                                  )}
                                              </optgroup>
                                            </select>
                                          </div>

                                          <button
                                            type="button"
                                            className={styles.fieldDel}
                                            title="Remove Field"
                                            disabled={fSaving}
                                            onClick={() => {
                                              const next = getFieldDraft(s.id, t).filter((_, j) => j !== i);
                                              setFieldDraft(s.id, t.name, next);
                                              saveFields(s.id, t.name, next);
                                            }}
                                          >
                                            {fSaving ? (
                                              <span className={`${styles.spinner} ${styles.spinnerField}`} />
                                            ) : (
                                              "✕"
                                            )}
                                          </button>
                                        </div>
                                      );
                                    })}

                                    {/* Add new column/field form */}
                                    <div className={styles.addColForm}>
                                      <span className={styles.addColFormTitle}>+ Add New Column to Monday Board</span>
                                      <div className={styles.addColFormFields}>
                                        <input
                                          className={styles.addColInput}
                                          value={newFieldForm[fieldKey(s.id, t.name)]?.ask || ""}
                                          placeholder="Column Title (e.g., Budget, Age)"
                                          onChange={(e) => {
                                            const key = fieldKey(s.id, t.name);
                                            setNewFieldForm((prev) => ({
                                              ...prev,
                                              [key]: {
                                                ...(prev[key] || { type: "text", sourceType: "ai_variable", sourceKey: "" }),
                                                ask: e.target.value
                                              }
                                            }));
                                          }}
                                        />

                                        <select
                                          className={styles.addColSelect}
                                          value={newFieldForm[fieldKey(s.id, t.name)]?.type || "text"}
                                          onChange={(e) => {
                                            const key = fieldKey(s.id, t.name);
                                            setNewFieldForm((prev) => ({
                                              ...prev,
                                              [key]: {
                                                ...(prev[key] || { ask: "", sourceType: "ai_variable", sourceKey: "" }),
                                                type: e.target.value
                                              }
                                            }));
                                          }}
                                        >
                                          <option value="text">Text</option>
                                          <option value="numbers">Numbers</option>
                                          <option value="date">Date</option>
                                          <option value="email">Email</option>
                                          <option value="phone">Phone</option>
                                          <option value="long_text">Long Text</option>
                                        </select>

                                        <select
                                          className={styles.addColSelect}
                                          value={(() => {
                                            const f = newFieldForm[fieldKey(s.id, t.name)] || {};
                                            return `${f.sourceType || "ai_variable"}::${f.sourceKey || ""}`;
                                          })()}
                                          onChange={(e) => {
                                            const selected = e.target.value;
                                            const key = fieldKey(s.id, t.name);
                                            const [sType, sKey] = selected.split("::");
                                            setNewFieldForm((prev) => ({
                                              ...prev,
                                              [key]: {
                                                ...(prev[key] || { ask: "", type: "text" }),
                                                sourceType: sType,
                                                sourceKey: sKey || ""
                                              }
                                            }));
                                          }}
                                        >
                                          <option value="ai_variable::">AI Variable (Auto from Title)</option>
                                          <optgroup label="System Call Metadata">
                                            <option value="system::caller_phone">Caller Phone</option>
                                            <option value="system::number_dialed">Number Dialed</option>
                                            <option value="system::call_date">Call Date & Time</option>
                                            <option value="system::duration">Call Duration</option>
                                            <option value="system::recording_url">Recording URL</option>
                                            <option value="system::call_status">Call Status</option>
                                            <option value="system::call_summary">Call Summary (AI)</option>
                                            <option value="system::call_transcript">Call Transcript</option>
                                            <option value="system::lead_type">Lead Type / Classification</option>
                                            <option value="system::user_sentiment">User Sentiment</option>
                                            <option value="system::call_type">Call Type</option>
                                            {/* Booked / No answer / Wrong number / Not qualified / Follow-up needed / Other */}
                                            <option value="system::call_result">Call Result</option>
                                          </optgroup>
                                          {questionVars.length > 0 && (
                                            <optgroup label="Qualifying Answers">
                                              {questionVars.map((v) => (
                                                <option key={v.key} value={`ai_variable::${v.key}`} title={v.label}>
                                                  Answer: {v.key}
                                                </option>
                                              ))}
                                            </optgroup>
                                          )}
                                          <optgroup label="AI Variable (Agent Collects)">
                                            {agentVars.map((vname) => (
                                              <option key={vname} value={`ai_variable::${vname}`}>AI Variable: {aiVarLabel(vname)}</option>
                                            ))}
                                            {/* Fallbacks if agentVars doesn't include common ones */}
                                            {!agentVars.includes("name") && <option value="ai_variable::name">AI Variable: Name</option>}
                                            {!agentVars.includes("phone") && <option value="ai_variable::phone">AI Variable: Secondary Phone</option>}
                                            {!agentVars.includes("email") && <option value="ai_variable::email">AI Variable: Email</option>}
                                            {!agentVars.includes("reason") && <option value="ai_variable::reason">AI Variable: Reason</option>}
                                            {!agentVars.includes("address") && <option value="ai_variable::address">AI Variable: Address</option>}
                                            {/* Appointment — call ke baad ke analysis se (book hui ho tabhi date/time bhare hote hain). */}
                                            {!agentVars.includes("appointment_booked") && <option value="ai_variable::appointment_booked">AI Variable: Appointment Booked</option>}
                                            {!agentVars.includes("appointment_datetime") && <option value="ai_variable::appointment_datetime">AI Variable: Appointment Date &amp; Time</option>}
                                            {!agentVars.includes("appointment_date") && <option value="ai_variable::appointment_date">AI Variable: Appointment Date</option>}
                                            {!agentVars.includes("appointment_time") && <option value="ai_variable::appointment_time">AI Variable: Appointment Time</option>}
                                          </optgroup>
                                        </select>

                                        <button
                                          type="button"
                                          className={styles.addColSubmitBtn}
                                          onClick={async () => {
                                            const key = fieldKey(s.id, t.name);
                                            const formVal = newFieldForm[key];
                                            if (!formVal || !formVal.ask || !formVal.ask.trim()) {
                                              showPopup("failed", "Please enter a column title");
                                              return;
                                            }

                                            const title = formVal.ask.trim();
                                            const currentFields = getFieldDraft(s.id, t);
                                            const isDuplicate = currentFields.some(
                                              (f) => String(f.ask).trim().toLowerCase() === title.toLowerCase()
                                            );
                                            if (isDuplicate) {
                                              showPopup("failed", `Column "${title}" is already mapped.`);
                                              return;
                                            }
                                            const type = formVal.type || "text";
                                            const sType = formVal.sourceType || "ai_variable";
                                            let sKey = formVal.sourceKey || "";

                                            if (sType === "ai_variable" && !sKey) {
                                              sKey = title.toLowerCase().replace(/[^a-z0-9]+/g, "_");
                                            }

                                            const newField = {
                                              ask: title,
                                              column: null, // backend will create column
                                              type,
                                              param: title.toLowerCase().replace(/[^a-z0-9]+/g, "_"),
                                              mapping: { sourceType: sType, sourceKey: sKey }
                                            };

                                            const next = [...getFieldDraft(s.id, t), newField];
                                            setFieldDraft(s.id, t.name, next);

                                            setNewFieldForm((prev) => ({
                                              ...prev,
                                              [key]: { ask: "", type: "text", sourceType: "ai_variable", sourceKey: "" }
                                            }));

                                            await saveFields(s.id, t.name, next);
                                          }}
                                        >
                                          Add Column
                                        </button>
                                      </div>
                                    </div>

                                    <span className={styles.fieldsHint}>
                                      Saved automatically — the mapping controls which call variables are pushed to Monday board columns.
                                    </span>
                                  </div>
                                )}
                              </div>
                              <label className={styles.switch}>
                                <input
                                  type="checkbox"
                                  checked={draft.has(t.name)}
                                  disabled={!agentId}
                                  onChange={() => toggleTool(s.id, t.name)}
                                />
                                <span className={styles.slider} />
                              </label>
                            </div>
                          );
                        })
                      )}
                    </div>
                    {(s.tools || []).length > 0 && (
                      <div className={styles.autoSaveRow}>
                        {busy || serverSaving ? (
                          <span className={styles.autoSaveBusy}>
                            <span className={`${styles.spinner} ${styles.spinnerDark}`} />
                            {serverSaving ? "Saving fields…" : "Saving…"}
                          </span>
                        ) : (
                          <span className={styles.autoSaveDone}>
                            ✓ Changes save automatically
                          </span>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add / Edit modal */}
      <NewModal
        isOpen={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        title={null}
        height="auto"
        bodyMaxHeight="90vh"
      >
        <div className={styles.modalInner}>
          <h3 className={styles.modalTitle}>
            {editingId ? "Edit MCP server" : "Add MCP server"}
          </h3>
          <p className={styles.modalSub}>
            {editingId
              ? "Update the connection. Leave the token blank to keep the current one."
              : "We'll connect and auto-discover the available tools."}
          </p>

          <div className={styles.field}>
            <label className={styles.label}>Label</label>
            <input
              className={styles.input}
              value={form.label}
              placeholder="e.g. Monday.com"
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>MCP Server URL *</label>
            <input
              className={styles.input}
              value={form.mcpUrl}
              placeholder="https://mcp.monday.com/mcp"
              onChange={(e) => setForm((f) => ({ ...f, mcpUrl: e.target.value }))}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>
              Bearer token {editingId ? "(leave blank to keep)" : "*"}
            </label>
            <input
              className={styles.input}
              type="password"
              value={form.authToken}
              placeholder="Paste API token / access token"
              onChange={(e) => setForm((f) => ({ ...f, authToken: e.target.value }))}
            />
            <p className={styles.hint}>
              Stored encrypted. Used as <code>Authorization: Bearer …</code> to
              the MCP server.
            </p>
          </div>

          {formErr && <p className={styles.errText}>{formErr}</p>}

          <div className={styles.modalActions}>
            <button
              className={styles.btnGhost}
              onClick={() => setModalOpen(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className={styles.btnPrimary}
              onClick={submitForm}
              disabled={saving || !form.mcpUrl.trim()}
            >
              {saving
                ? editingId
                  ? "Saving…"
                  : "Connecting…"
                : editingId
                  ? "Save"
                  : "Add & discover"}
            </button>
          </div>
        </div>
      </NewModal>

      {/* Custom Popup */}
      {popupInfo.show && (
        <PopUp
          type={popupInfo.type}
          message={popupInfo.message}
          onClose={() => setPopupInfo({ ...popupInfo, show: false })}
        />
      )}
    </div>
  );
};

export default McpIntegration;
